import React, { useState, useEffect } from 'react';
import { app } from '../firebase'; // Importez votre configuration Firebase
import { getFirestore, collection, onSnapshot, orderBy, query, doc, updateDoc, where, deleteField, Timestamp } from 'firebase/firestore'; 
import Loader from '../components/Loader';
import { isCoownership, isProvider, isRentalManager, useUser } from '../contexts/UserContext';
import { dateFromTimestampToString } from '../librairies/Dates';
import TextModal from '../components/modals/TextModal';
import DeleteModal from '../components/modals/DeleteModal';
import {Icon} from 'react-icons-kit';
import {filter} from 'react-icons-kit/feather/filter';
import { rentalManagerCanMakeActionOnCoownership } from '../librairies/UsersLib';
import ReplyClaimModal from './ReplyClaimModal';
import AppDisplayPreview from '../components/AppDisplayPreview';
import demandesPreview from '../assets/images/app-display/demandes.jpeg';

const db = getFirestore(app);

const REPLY_TITLES = {
  prestataire: "Réponse du prestataire",
  gestionnaire: "Réponse du gestionnaire",
  syndic: "Réponse du syndic",
};

const STATUS_RANK = {
  activated: 0,
  deal: 1,
  canceled: 2,
  archived: 3,
};

const compareByStatus = (a, b) => {
  const rankA = STATUS_RANK[a.status] ?? 0;
  const rankB = STATUS_RANK[b.status] ?? 0;
  if (rankA !== rankB) {
    return rankA - rankB;
  }
  return b.date - a.date;
};

const ListClaims = () => {

  const [claims, setClaims] = useState([]); 
  const [loader, setLoader] = useState(true);

  const [showTextModal, setShowTextModal] = useState(false); 
  const [descriptionToShow, setDescriptionToShow] = useState(null); 
  const [filterDate, setFilterDate] = useState(true); 
  const [filterProvider, setFilterProvider] = useState(false);
  const [filterStatus, setFilterStatus] = useState(false);
  const [claimToReply, setClaimToReply] = useState(null);
  const [claimReplyToDelete, setClaimReplyToDelete] = useState(null);
  const [claimToHide, setClaimToHide] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);

  //contexte de l'utilisateur connecté
  const user = useUser();


  const providerUid = isProvider(user.user) ? user.user.user.uid : null;

  useEffect(() => {
    const collectionRef = query(
      collection(db, user.user.userData.lastCoownershipLoaded.path+"/claims"),
      ...(providerUid ? [where("userProviderId", "==", providerUid)] : []),
      ...(filterDate && !filterStatus ? [orderBy("dateCreation", "desc")] : []),
      ...(filterProvider && !filterDate && !filterStatus ? [orderBy("nameProvider")] : [])
    );


    const unsubscribe = onSnapshot(collectionRef, (snapshot) => {

      if(snapshot.docs.length > 0){
        
        let arr = [];

        snapshot.docs.map((claim) => {
          if (claim.data().hiddenFromBackOffice === true) {
            return;
          }

          let obj = {
            id: claim.id, 
            object: claim.data().object, 
            text: claim.data().text, 
            firstname: claim.data().firstnameCreator,
            lastname: claim.data().lastnameCreator,
            date: claim.data().dateCreation.seconds,
            status: claim.data().status || 'activated', // Valeur par défaut si pas de statut
            nameProvider: claim.data().nameProvider,
            image: claim.data().image,
            userProviderId: claim.data().userProviderId ?? null,
            isPrivate: claim.data().isPrivate === true,
            reply: claim.data().reply || "",
            replyDate: claim.data().replyDate?.seconds ?? null,
            replyAuthor: claim.data().replyAuthor || null,
          }

          arr.push(obj);

        });

        if (filterStatus) {
          arr.sort(compareByStatus);
        }
        
        setClaims(arr);
      } else {
        setClaims([]);
      }

      setLoader(false);
    }, (error) => {
      console.error("Erreur lors du chargement des demandes:", error);
      setLoader(false);
    });

    return () => {
      console.log("unsubscribe claims");
      unsubscribe();
    }
  }, [filterDate, filterProvider, filterStatus, providerUid]); 


  useEffect(() => {
    if(showTextModal === false){
      setDescriptionToShow(null);
    }
  }, [showTextModal]);

  useEffect(() => {
    if(descriptionToShow != null){
      setShowTextModal(true); 
    }
  }, [descriptionToShow]);

  /**
   * Met à jour le statut d'une claim
   * @param {string} claimId - ID de la claim
   * @param {string} newStatus - Nouveau statut
   */
  const updateClaimStatus = async (claimId, newStatus) => {
    if (providerUid) {
      const claim = claims.find((item) => item.id === claimId);
      if (!claim || claim.userProviderId !== providerUid) {
        alert("Impossible de mettre à jour le statut");
        return;
      }
    }

    try {
      const claimRef = doc(db, user.user.userData.lastCoownershipLoaded.path+"/claims", claimId);
      await updateDoc(claimRef, {
        status: newStatus
      });
      
      alert("Le statut a été mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      alert("Impossible de mettre à jour le statut");
    }
  }

  const canReply = rentalManagerCanMakeActionOnCoownership(user.user);

  const replyAuthor = () => {
    if (isProvider(user.user)) {
      return "prestataire";
    }
    if (isRentalManager(user.user)) {
      return "gestionnaire";
    }
    if (isCoownership(user.user)) {
      return "syndic";
    }
    return null;
  };

  const canEditClaim = (claim) => {
    if (!canReply || !replyAuthor()) {
      return false;
    }
    if (providerUid && claim.userProviderId !== providerUid) {
      return false;
    }
    return true;
  };

  const saveReply = async (text) => {
    if (!claimToReply || !canEditClaim(claimToReply)) {
      alert("Impossible d'enregistrer la réponse");
      return;
    }

    try {
      setReplyLoading(true);
      const claimRef = doc(db, user.user.userData.lastCoownershipLoaded.path+"/claims", claimToReply.id);
      await updateDoc(claimRef, {
        reply: text,
        replyDate: Timestamp.now(),
        replyAuthor: replyAuthor(),
      });
      setClaimToReply(null);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la réponse:", error);
      alert("Impossible d'enregistrer la réponse");
    } finally {
      setReplyLoading(false);
    }
  };

  const deleteReply = async () => {
    if (!claimReplyToDelete || !canEditClaim(claimReplyToDelete)) {
      alert("Impossible de supprimer la réponse");
      return;
    }

    try {
      const claimRef = doc(db, user.user.userData.lastCoownershipLoaded.path+"/claims", claimReplyToDelete.id);
      await updateDoc(claimRef, {
        reply: deleteField(),
        replyDate: deleteField(),
        replyAuthor: deleteField(),
      });
      setClaimReplyToDelete(null);
    } catch (error) {
      console.error("Erreur lors de la suppression de la réponse:", error);
      alert("Impossible de supprimer la réponse");
    }
  };

  const hideArchivedClaim = async () => {
    if (!claimToHide || claimToHide.status !== "archived" || !canEditClaim(claimToHide)) {
      alert("Impossible de supprimer cette demande");
      return;
    }

    try {
      const claimRef = doc(db, user.user.userData.lastCoownershipLoaded.path+"/claims", claimToHide.id);
      await updateDoc(claimRef, {
        hiddenFromBackOffice: true,
      });
      setClaimToHide(null);
    } catch (error) {
      console.error("Erreur lors du masquage de la demande:", error);
      alert("Impossible de supprimer cette demande");
    }
  };

  const handleDateFilterToggle = () => {
    const nextFilterDate = !filterDate;
    setFilterDate(nextFilterDate);
    if (nextFilterDate) {
      setFilterProvider(false);
      setFilterStatus(false);
    }
  };

  const handleProviderFilterToggle = () => {
    const nextFilterProvider = !filterProvider;
    setFilterProvider(nextFilterProvider);
    setFilterDate(!nextFilterProvider);
    setFilterStatus(false);
  };

  const handleStatusFilterToggle = () => {
    const nextFilterStatus = !filterStatus;
    setFilterStatus(nextFilterStatus);
    if (nextFilterStatus) {
      setFilterDate(false);
      setFilterProvider(false);
    } else {
      setFilterDate(true);
      setFilterProvider(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end">
        <AppDisplayPreview image={demandesPreview} label="Aperçu des demandes dans l'application" />
      </div>
      {loader ? (
        <Loader />
      ) : (
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                
                <th scope="col" className="px-6 py-3 cursor-pointer" onClick={handleDateFilterToggle}>
                    Date                 
                    <span className={filterDate ? "text-primary" : "text-black"}>
                      <Icon className="ml-1" icon={filter} size={16} />
                    </span>
                </th>

                <th scope="col" className="px-6 py-3">
                    Copropriétaire
                </th>
                <th scope="col" className="px-6 py-3 cursor-pointer" onClick={handleProviderFilterToggle}>
                    Société
                    <span className={filterProvider ? "text-primary" : "text-black"}>
                      <Icon className="ml-1" icon={filter} size={16} />
                    </span>
                </th>
                <th scope="col" className="px-6 py-3">
                    Objet
                </th>
                <th scope="col" className="px-6 py-3">
                    Privée
                </th>
                <th scope="col" className="px-6 py-3 cursor-pointer" onClick={handleStatusFilterToggle}>
                    Statut
                    <span className={filterStatus ? "text-primary" : "text-black"}>
                      <Icon className="ml-1" icon={filter} size={16} />
                    </span>
                </th>
                <th scope="col" className="px-6 py-3">
                    Action
                </th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim, key) => {
                const rowClass = key % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-gray-50 dark:bg-gray-800";
                const hasReply = Boolean(claim.reply);

                return [
                  <tr key={claim.id} className={`${rowClass} ${hasReply ? "" : "border-b"} dark:border-gray-700`}>
                    <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                      {dateFromTimestampToString(claim.date)}
                    </th>
                    <td className="px-6 py-4">
                      {claim.firstname + " " + claim.lastname}
                    </td>
                    <td className="px-6 py-4">
                      {claim.nameProvider}
                    </td>
                    <td className="px-6 py-4">
                      {claim.object}
                    </td>
                    <td className="px-6 py-4">
                      {claim.isPrivate ? "Oui" : "Non"}
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={claim.status} 
                        disabled={!rentalManagerCanMakeActionOnCoownership(user.user)}
                        onChange={(e) => updateClaimStatus(claim.id, e.target.value)}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 min-w-[120px] whitespace-nowrap"
                        style={{ minWidth: '120px', maxWidth: '150px' }}
                      >
                        <option value="activated">En cours</option>
                        <option value="deal">Traitée</option>
                        <option value="canceled">Annulée</option>
                        <option value="archived">Archivée</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className='flex gap-4'>
                        <a href="#" onClick={() => setDescriptionToShow(claim.text)} className="font-medium text-blue-600 dark:text-blue-500 hover:underline">Afficher la description</a>
                        {/*<a href="#" onClick={() => console.log(claim.id)} className="font-medium text-red-600 dark:text-red-500 hover:underline">Archiver</a>*/}
                        {claim.image && (
                          <a href={claim.image} target='_blank' className="font-medium text-blue-600 dark:text-blue-500 hover:underline">Voir l'image</a>
                        )}
                        {canEditClaim(claim) && !claim.reply ? (
                          <a href="#" onClick={() => setClaimToReply(claim)} className="font-medium text-blue-600 dark:text-blue-500 hover:underline">Répondre</a>
                        ) : null}
                        {canEditClaim(claim) && claim.reply ? (
                          <a href="#" onClick={() => setClaimToReply(claim)} className="font-medium text-blue-600 dark:text-blue-500 hover:underline">Modifier la réponse</a>
                        ) : null}
                        {canEditClaim(claim) && claim.reply ? (
                          <a href="#" onClick={() => setClaimReplyToDelete(claim)} className="font-medium text-red-600 dark:text-red-500 hover:underline">Supprimer la réponse</a>
                        ) : null}
                        {canEditClaim(claim) && claim.status === "archived" ? (
                          <a href="#" onClick={() => setClaimToHide(claim)} className="font-medium text-red-600 dark:text-red-500 hover:underline">Supprimer</a>
                        ) : null}
                      </div>
                    </td>
                  </tr>,
                  hasReply ? (
                    <tr key={`${claim.id}-reply`} className={`${rowClass} border-b dark:border-gray-700`}>
                      <td colSpan={7} className="px-6 pb-4 pt-1">
                        <div className="ml-4 border-l-4 border-secondary bg-[rgba(123,207,177,0.2)] rounded-r-lg px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                            {REPLY_TITLES[claim.replyAuthor] || "Réponse"}
                            {claim.replyDate ? ` le ${dateFromTimestampToString(claim.replyDate)}` : ""}
                          </p>
                          <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">{claim.reply}</p>
                        </div>
                      </td>
                    </tr>
                  ) : null,
                ]
              })}
            </tbody>
          </table>
        </div>
      )}

      {showTextModal ? (
        <TextModal onClose={() => setShowTextModal(false)} text={descriptionToShow}/>
      ) : null }

      {claimToReply ? (
        <ReplyClaimModal
          onClose={() => setClaimToReply(null)}
          onSubmit={saveReply}
          initialText={claimToReply.reply}
          replyAuthor={claimToReply.replyAuthor}
          loading={replyLoading}
        />
      ) : null }

      {claimReplyToDelete ? (
        <DeleteModal closeAction={() => setClaimReplyToDelete(null)} confirmAction={deleteReply} />
      ) : null }

      {claimToHide ? (
        <DeleteModal closeAction={() => setClaimToHide(null)} confirmAction={hideArchivedClaim} />
      ) : null }
    
    </div>
  );
};

export default ListClaims;
